import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import supabase from '../supabase';
import { useSiteLang as useLang } from '../siteLang';
import RetroVideoPlayer from '../components/RetroVideoPlayer';

function Campaign() {
    const navigate = useNavigate();
    const { year } = useParams();
    const { lang } = useLang();
    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [videos, setVideos] = useState([]);
    const [activeVideo, setActiveVideo] = useState(null);

    useEffect(() => {
        if (!campaign) return;
        document.title = `Campaign ${campaign.year} | winston.ie`;
        return () => { document.title = 'winston.ie'; };
    }, [campaign]);

    useEffect(() => {
        const fetchVideos = async () => {
            const { data } = await supabase
                .from('campaign_videos')
                .select('*')
                .eq('campaign_year', Number(year))
                .order('order_index', { ascending: true });
            if (data) setVideos(data);
        };
        fetchVideos();
    }, [year]);

    const archiveLinkText = {
        fr: 'Voir toutes les campagnes →',
        en: 'View all campaigns →',
        vi: 'Xem tất cả các chiến dịch →',
    }[lang] || 'Voir toutes les campagnes →';

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        setCampaign(null);

        const fetchCampaign = async () => {
            const { data, error } = await supabase
                .from('campaigns')
                .select('*')
                .eq('year', Number(year))
                .maybeSingle();

            if (error) {
                console.error('Erreur fetch campaign:', error.message);
            }

            if (data) {
                setCampaign(data);
            } else {
                setNotFound(true);
            }
            setLoading(false);
        };

        fetchCampaign();
    }, [year]);

    if (loading) {
        return <div style={{ minHeight: '100vh', background: '#0e1013' }} />;
    }

    if (notFound || !campaign) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#0e1013' }}>
                <Navbar variant="dark" />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ color: 'rgba(244,239,228,0.4)', fontFamily: 'Helvetica Neue, sans-serif', fontSize: '13px' }}>
                        Aucune campagne trouvée pour {year}.
                    </p>
                </div>
                <Footer variant="dark" />
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#0e1013' }}>

            <Navbar variant="dark" />

            <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%', padding: '8px 24px 0' }}>
                <p
                    onClick={() => navigate('/')}
                    className="wlink"
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 300, fontSize: '13px',
                        letterSpacing: '0.04em', color: 'rgba(244,239,228,0.45)',
                        margin: '16px 0 0',
                    }}
                >
                    ← Home
                </p>
            </div>

            <div style={{ maxWidth: '900px', margin: '32px auto 0', width: '100%', padding: '0 24px' }}>
                <div style={{ width: '100%', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,0.5)' }}>
                    <img src={campaign.image_url} alt={`Campaign ${campaign.year}`} style={{ width: '100%', display: 'block' }} />
                </div>
            </div>

            <div style={{ maxWidth: '640px', margin: '0 auto', width: '100%', padding: '48px 24px 40px', textAlign: 'center' }}>
                <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontSize: '10px', letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(244,239,228,0.4)', margin: '0 0 18px' }}>
                    Campaign {campaign.year}
                </p>

                <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 300, fontSize: '16px', color: 'rgba(244,239,228,0.85)', margin: '0 0 6px' }}>
                    {campaign.tagline}
                </p>
                <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontSize: '11px', color: 'rgba(244,239,228,0.4)', margin: '0 0 40px' }}>
                    {campaign.tagline_vi}
                </p>

                <div style={{ width: '32px', height: '1px', background: 'rgba(244,239,228,0.2)', margin: '0 auto 40px' }} />

                {campaign.story ? (
                    <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 300, fontSize: '17px', lineHeight: 1.75, color: 'rgba(244,239,228,0.85)', margin: '0 0 40px' }}>
                        {campaign.story}
                    </p>
                ) : (
                    <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontWeight: 300, fontSize: '15px', lineHeight: 1.75, color: 'rgba(244,239,228,0.35)', margin: '0 0 40px' }}>
                        The story behind this year's campaign — coming soon.
                    </p>
                )}

                {(campaign.photographer_name || campaign.location) && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                        {campaign.photographer_name && (
                            <p
                                onClick={() => campaign.photographer_username && navigate(`/creator/${campaign.photographer_username}`)}
                                style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontSize: '12.5px', color: 'rgba(244,239,228,0.65)', cursor: campaign.photographer_username ? 'pointer' : 'default', margin: 0 }}
                            >
                                Photography by {campaign.photographer_name}
                            </p>
                        )}
                        {campaign.location && (
                            <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontSize: '10.5px', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(244,239,228,0.35)', margin: 0 }}>
                                {campaign.location}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Section Films — maintenant bien à l'intérieur du JSX */}
            {videos.length > 0 && (
                <div style={{ maxWidth: '640px', margin: '0 auto', width: '100%', padding: '0 24px 48px' }}>
                    <p style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif', fontSize: '10px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(244,239,228,0.4)', textAlign: 'center', marginBottom: '24px' }}>
                        Films
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(videos.length, 3)}, 1fr)`, gap: '14px' }}>
                        {videos.map(v => (
                            <div
                                key={v.id}
                                onClick={() => setActiveVideo(v)}
                                style={{
                                    aspectRatio: '9 / 16',
                                    background: '#000',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    position: 'relative',
                                }}
                            >
                                <video src={v.video_url} muted style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.6 }} />
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <span style={{ color: '#fff', fontSize: '14px', marginLeft: '2px' }}>▶</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ maxWidth: '640px', margin: '0 auto', width: '100%', padding: '0 24px 64px', textAlign: 'center' }}>
                <p
                    onClick={() => navigate('/campaigns')}
                    className="wlink"
                    style={{
                        display: 'inline-block',
                        fontFamily: 'Helvetica Neue, Arial, sans-serif',
                        fontWeight: 300,
                        fontSize: '13px',
                        letterSpacing: '0.04em',
                        color: 'rgba(244,239,228,0.5)',
                    }}
                >
                    {archiveLinkText}
                </p>
            </div>

            <Footer variant="dark" />

            {/* Popup Windows rétro — aussi bien à l'intérieur du JSX maintenant */}
            {activeVideo && (
                <div
                    onClick={() => setActiveVideo(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(27,27,24,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: 'min(400px, 100%)',
                            background: '#f4f1ec',
                            border: '2px solid #fff',
                            borderRight: '2px solid #8a8680',
                            borderBottom: '2px solid #8a8680',
                            boxShadow: '6px 6px 0 rgba(0,0,0,0.4)',
                            fontFamily: "'MS Sans Serif', 'Tahoma', Arial, sans-serif",
                        }}
                    >
                        <div style={{
                            background: 'linear-gradient(90deg, #1b1b18, #3a3632)',
                            color: '#f4f1ec', padding: '5px 8px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: '13px', fontWeight: 'bold',
                        }}>
                            <span>{activeVideo.title || `campaign_${activeVideo.campaign_year}.mp4`}</span>
                            <div
                                onClick={() => setActiveVideo(null)}
                                style={{ width: '16px', height: '14px', background: '#f4f1ec', border: '1px solid #fff', borderRight: '1px solid #8a8680', borderBottom: '1px solid #8a8680', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#1b1b18', cursor: 'pointer' }}
                            >
                                ✕
                            </div>
                        </div>

                        <RetroVideoPlayer src={activeVideo.video_url} />

                    </div>
                </div>
            )}
        </div>
    );
}

export default Campaign;